import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import QuickBooksMappingModal from "./QuickBooksMappingModal";
import QuickBooksRoutingModal from "./QuickBooksRoutingModal";
import {
  backend_url,
  
  getQuickBooksConfigURL,
  saveQuickBooksConfigURL,
  updateQuickBooksConfigURL,
  deleteQuickBooksConfigURL,
  connectQuickBooksURL,
  disconnectQuickBooksURL,
  runQuickBooksOutboundSyncURL,
  getQuickBooksOutboundSyncStatusURL,
  getEtimsConfigURL,
  saveEtimsConfigURL,
  updateEtimsConfigURL,
  disconnectEtimsURL,
  getWaterSensorSettingsURL,
  updateWaterSensorSettingsURL,
} from "../../../utils/urls";
import QuickBooksAccountMapping from "./QuickBooksAccountMapping";
import QuickBooksItemMapping from "./QuickBooksItemMapping";
import QuickBooksPaymentChannelMapping from "./QuickBooksPaymentChannelMapping";
import QuickBooksInvoicePaymentSync from "./QuickBooksInvoicePaymentSync";
import QuickBooksCustomerSync from "./QuickBooksCustomerSync";
import QuickBooksBackfill from "./QuickBooksBackfill";
import QuickBooksOutboundSync from "./QuickBooksOutboundSync";
import QuickBooksVendorSync from "./QuickBooksVendorSync";
import ZohoIntegration from "./ZohoIntegration";

// ─── shared helpers ───────────────────────────────────────────────────────────
const maskMiddle = (str, start = 4, end = 4, maskChar = "*") => {
  if (!str || typeof str !== "string") return "";
  const len = str.length;
  if (len <= start + end) return maskChar.repeat(Math.max(0, len));
  const head = str.slice(0, start);
  const tail = str.slice(len - end);
  return `${head}${maskChar.repeat(len - start - end)}${tail}`;
};

const showMaskedOrNA = (v) => {
  const s = typeof v === "string" ? v : "";
  return s ? maskMiddle(s) : "—";
};

// ─── QuickBooks multi-realm helpers ───────────────────────────────────────────
const QUICKBOOKS_FORM_EMPTY = {
  label: "",
  clientId: "",
  clientSecret: "",
  realmId: "",
  environment: "sandbox",
};

const INTEGRATION_TABS = [
  { id: "zoho", label: "Zoho Books", icon: "ti-books" },
  { id: "quickbooks", label: "QuickBooks", icon: "ti-brand-quickbooks" },
  { id: "etims", label: "KRA eTIMS", icon: "ti-receipt-tax" },
  { id: "sensors", label: "IoT Services", icon: "ti-droplet" },
];

// ─── eTims sub-component ──────────────────────────────────────────────────────
const ETIMS_EMPTY = {
  tin: "",
  branchId: "00",
  enabled: true,
  baseUrl: "",
  username: "",
  password: "",
};

const EtimsIntegrationTab = ({ facilityId }) => {
  const [config, setConfig]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [formData, setFormData]     = useState(ETIMS_EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (facilityId) loadConfig();
  }, [facilityId]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(getEtimsConfigURL(facilityId), "GET");
      if (response?.success && response.data?.success) {
        setConfig(response.data.data.config);
      } else {
        setConfig(null);
      }
    } catch (err) {
      console.error("Error loading eTims config:", err);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const url = config
        ? updateEtimsConfigURL(facilityId)
        : saveEtimsConfigURL(facilityId);

      const response = await makeRequest2(url, "POST", formData);

      if (response?.success && response.data?.success) {
        toastify(
          "success",
          response.data.message || "eTims configuration saved successfully",
        );
        setShowForm(false);
        setFormData(ETIMS_EMPTY);
        loadConfig();
      } else {
        toastify(
          "error",
          response?.data?.error ||
            response?.data?.message ||
            response?.error ||
            "Failed to save eTims configuration",
        );
      }
    } catch (err) {
      console.error("Error saving eTims config:", err);
      toastify("error", "Failed to save eTims configuration");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading(true);
      const url = `${getEtimsConfigURL(facilityId)}?unmask=true`;
      const response = await makeRequest2(url, "GET");

      if (response?.success && response.data?.success && response.data.data) {
        const c = response.data.data.config;
        setFormData({
          tin:      c.tin      || "",
          branchId: c.branchId || "00",
          enabled:  c.enabled  ?? true,
          baseUrl:  c.baseUrl  || "",
          username: c.username || "",
          password: "", // never pre-fill password
        });
        setShowForm(true);
      } else {
        toastify(
          "error",
          response?.data?.error ||
            response?.data?.message ||
            "Failed to fetch eTims credentials",
        );
      }
    } catch (err) {
      console.error("Error fetching eTims credentials for edit:", err);
      toastify("error", err.message || "Failed to fetch credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to remove the eTims configuration?")) return;
    try {
      const response = await makeRequest2(disconnectEtimsURL(facilityId), "POST");
      if (response?.success && response.data?.success) {
        toastify("success", response.data.message || "eTims configuration removed");
        setConfig(null);
        loadConfig();
      } else {
        toastify(
          "error",
          response?.data?.error ||
            response?.data?.message ||
            "Failed to remove eTims configuration",
        );
      }
    } catch (err) {
      console.error("Error removing eTims config:", err);
      toastify("error", "Failed to remove eTims configuration");
    }
  };

  return (
    <div className="col-lg-6 col-md-12 mb-4">
      <div className="card">
        <div className="card-body">

          {/* Card Header */}
          <div className="d-flex align-items-start mb-3">
            <div
              className="avtar avtar-l bg-light-warning flex-shrink-0 me-3"
              style={{ width: "60px", height: "60px" }}
            >
              <i
                className="ti ti-receipt-tax"
                style={{ fontSize: "32px", color: "#e65c00" }}
              ></i>
            </div>
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h5 className="mb-1">KRA eTims</h5>
                  <p className="text-muted mb-0 small">
                    Electronic tax invoice submission to KRA
                  </p>
                </div>
                {config && config.enabled && (
                  <span className="badge bg-success">
                    <i className="ti ti-check me-1"></i>Active
                  </span>
                )}
                {config && !config.enabled && (
                  <span className="badge bg-secondary">
                    <i className="ti ti-pause me-1"></i>Disabled
                  </span>
                )}
                {!config && !showForm && (
                  <span className="badge bg-light text-dark">Not Configured</span>
                )}
              </div>
            </div>
          </div>

          {/* Loading */}
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <>
              {/* ── CONFIGURED STATE ── */}
              {config && !showForm && (
                <div>
                  <div
                    className={`alert ${config.enabled ? "alert-success" : "alert-secondary"} mb-3`}
                    role="alert"
                  >
                    <i className={`ti ${config.enabled ? "ti-circle-check" : "ti-circle-off"} me-2`}></i>
                    {config.enabled
                      ? "eTims is configured and active"
                      : "eTims is configured but currently disabled"}
                  </div>

                  <div className="row mb-3">
                    <div className="col-6">
                      <small className="text-muted">KRA TIN</small>
                      <p className="mb-0 fw-medium">{config.tin}</p>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Branch ID</small>
                      <p className="mb-0 fw-medium">{config.branchId || "00"}</p>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-12">
                      <small className="text-muted">Credentials</small>
                      <div className="d-flex flex-column small">
                        <span>
                          <strong>Base URL:</strong>{" "}
                          {config.baseUrl ? (
                            config.baseUrl
                          ) : (
                            <span className="text-muted fst-italic">using system default</span>
                          )}
                        </span>
                        <span>
                          <strong>Username:</strong>{" "}
                          {config.username ? (
                            showMaskedOrNA(config.username)
                          ) : (
                            <span className="text-muted fst-italic">using system default</span>
                          )}
                        </span>
                        <span>
                          <strong>Password:</strong>{" "}
                          {config.password ? (
                            showMaskedOrNA(config.password)
                          ) : (
                            <span className="text-muted fst-italic">using system default</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-6">
                      <small className="text-muted">Last Sync</small>
                      <p className="mb-0 fw-medium">
                        {config.syncStats?.lastSyncAt
                          ? new Date(config.syncStats.lastSyncAt).toLocaleString()
                          : "Never"}
                      </p>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Total Synced</small>
                      <p className="mb-0 fw-medium">
                        {config.syncStats?.totalSynced ?? 0}
                        {config.syncStats?.lastSyncStatus && (
                          <span
                            className={`ms-2 badge ${
                              config.syncStats.lastSyncStatus === "success"
                                ? "bg-success-subtle text-success"
                                : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {config.syncStats.lastSyncStatus}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {config.configuredBy && (
                    <div className="mb-3">
                      <small className="text-muted">
                        Last configured by:{" "}
                        <span className="fw-medium">{config.configuredBy}</span>
                        {config.lastUpdatedAt && (
                          <> · {new Date(config.lastUpdatedAt).toLocaleDateString()}</>
                        )}
                      </small>
                    </div>
                  )}

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary flex-fill"
                      onClick={handleEdit}
                    >
                      <i className="ti ti-edit me-1"></i>Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger flex-fill"
                      onClick={handleDisconnect}
                    >
                      <i className="ti ti-trash me-1"></i>Remove
                    </button>
                  </div>
                </div>
              )}

              {/* ── FORM STATE (new or editing) ── */}
              {(!config || showForm) && (
                <div>
                  {showForm && config && (
                    <div className="alert alert-info mb-3" role="alert">
                      <i className="ti ti-info-circle me-2"></i>
                      Update your eTims configuration
                    </div>
                  )}

                  <form onSubmit={handleSave}>
                    <div className="mb-3">
                      <label className="form-label">
                        KRA TIN <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="tin"
                        value={formData.tin}
                        onChange={handleChange}
                        placeholder="P051201909L"
                        required
                      />
                      <small className="form-text text-muted">
                        Kenya Revenue Authority Tax Identification Number
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Branch ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="branchId"
                        value={formData.branchId}
                        onChange={handleChange}
                        placeholder="00"
                        required
                      />
                      <small className="form-text text-muted">
                        Use <code>00</code> for headquarters
                      </small>
                    </div>

                    <hr className="my-3" />
                    <p className="text-muted small mb-3">
                      <i className="ti ti-settings me-1"></i>
                      <strong>Optional overrides</strong> — leave blank to use system defaults
                    </p>

                    <div className="mb-3">
                      <label className="form-label">Base URL</label>
                      <input
                        type="text"
                        className="form-control"
                        name="baseUrl"
                        value={formData.baseUrl}
                        onChange={handleChange}
                        placeholder="http://102.217.144.50:8888/api/v1/"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        placeholder="admin"
                        autoComplete="off"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Password{" "}
                        {config && (
                          <span className="text-muted">(leave blank to keep current)</span>
                        )}
                      </label>
                      <input
                        type="password"
                        className="form-control"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder={config ? "Leave blank to keep current" : "Enter password"}
                        autoComplete="new-password"
                      />
                    </div>

                    <div className="mb-3 form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="etimsEnabled"
                        name="enabled"
                        checked={formData.enabled}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="etimsEnabled">
                        Enable eTims integration
                      </label>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-success flex-fill"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Saving…
                          </>
                        ) : (
                          <>
                            <i className="ti ti-device-floppy me-1"></i>
                            {config ? "Update" : "Save Configuration"}
                          </>
                        )}
                      </button>
                      {showForm && config && (
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => {
                            setShowForm(false);
                            setFormData(ETIMS_EMPTY);
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────
// ─── Water Sensor sub-component ───────────────────────────────────────────────
const WaterSensorIntegrationCard = ({ facilityId }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (facilityId) loadSettings();
  }, [facilityId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(getWaterSensorSettingsURL(facilityId), "GET");
      if (response?.success && response.data?.success) {
        setEnabled(Boolean(response.data.data?.enabled));
      } else {
        setEnabled(false);
      }
    } catch (err) {
      console.error("Error loading water sensor settings:", err);
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    const next = !enabled;
    try {
      setSaving(true);
      const response = await makeRequest2(
        updateWaterSensorSettingsURL(facilityId),
        "PUT",
        { enabled: next },
      );
      if (response?.success && response.data?.success) {
        setEnabled(Boolean(response.data.data?.enabled));
        toastify(response.data.message || "Water sensor settings saved", "success");
      } else {
        toastify(response?.error || "Failed to update water sensor settings", "error");
      }
    } catch (err) {
      console.error("Error updating water sensor settings:", err);
      toastify("Failed to update water sensor settings", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="col-lg-6 col-md-12 mb-4">
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-start mb-3">
            <div
              className="avtar avtar-l bg-light-primary flex-shrink-0 me-3"
              style={{ width: "60px", height: "60px" }}
            >
              <i className="ti ti-ripple" style={{ fontSize: "32px" }}></i>
            </div>
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h5 className="mb-1">Water Sensor</h5>
                  <p className="text-muted mb-0 small">
                    LoRa water level sensors for monitoring water tanks
                  </p>
                </div>
                {!loading && (
                  enabled ? (
                    <span className="badge bg-success">
                      <i className="ti ti-check me-1"></i>Active
                    </span>
                  ) : (
                    <span className="badge bg-secondary">
                      <i className="ti ti-pause me-1"></i>Disabled
                    </span>
                  )
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-center">
              <p className="text-muted small mb-0 me-3">
                When enabled, a Water Tanks section appears under Meters Management.
              </p>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="waterSensorEnabled"
                  checked={enabled}
                  disabled={saving}
                  onChange={handleToggle}
                  style={{ cursor: saving ? "wait" : "pointer" }}
                />
                <label className="form-check-label" htmlFor="waterSensorEnabled">
                  {enabled ? "Enabled" : "Disabled"}
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const IntegrationsTab = ({ facilityId }) => {
  const [activeIntegration, setActiveIntegration] = useState(() => {
    try {
      return window.localStorage.getItem("integrations_active_tab") || "zoho";
    } catch (e) {
      return "zoho";
    }
  });
  const chooseIntegration = (id) => {
    setActiveIntegration(id);
    try {
      window.localStorage.setItem("integrations_active_tab", id);
    } catch (e) {
      /* storage unavailable */
    }
  };

  // QuickBooks State (multi-realm: a facility can have N QuickBooks
  // connections/companies now, not just one)
  const [quickBooksConnections, setQuickBooksConnections] = useState([]);
  const [quickBooksLoading, setQuickBooksLoading] = useState(true);
  const [showQuickBooksModal, setShowQuickBooksModal] = useState(false);
  const [showQuickBooksForm, setShowQuickBooksForm] = useState(false);
  const [editingQuickBooksId, setEditingQuickBooksId] = useState(null); // null => "create" mode
  const [quickBooksFormData, setQuickBooksFormData] = useState(QUICKBOOKS_FORM_EMPTY);
  const [quickBooksSubmitting, setQuickBooksSubmitting] = useState(false);

  // Account/Item/Payment-channel mapping modal — scoped to a single
  // connected realm (qbConfigId) at a time.
  const [showRoutingModal, setShowRoutingModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingQbConfigId, setMappingQbConfigId] = useState(null);
  const [mappingRealmLabel, setMappingRealmLabel] = useState("");

  // Manual "Sync Now" — catch-up sync for invoices/payments/customers/vendors
  // not yet pushed to a given realm. Fire-and-forget on the backend; this
  // modal polls status until every entity leaves "running".
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncQbConfigId, setSyncQbConfigId] = useState(null);
  const [syncRealmLabel, setSyncRealmLabel] = useState("");
  const [syncStarting, setSyncStarting] = useState(false);
  const [syncPolling, setSyncPolling] = useState(false);
  const [syncByEntity, setSyncByEntity] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [expandedSyncEntity, setExpandedSyncEntity] = useState(null);

  useEffect(() => {
    if (facilityId) loadQuickBooksConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const qbStatus = urlParams.get("qb");

    if (qbStatus === "success") {
      toastify("success", "QuickBooks connected successfully!");
      loadQuickBooksConfig();
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (qbStatus === "error") {
      toastify("error", "Failed to connect QuickBooks. Please try again.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // ==================== QUICKBOOKS FUNCTIONS (multi-realm) ====================

  const extractQuickBooksList = (backendData) => {
    const d = backendData?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.configs)) return d.configs;
    if (Array.isArray(d?.config)) return d.config;
    return [];
  };

  const loadQuickBooksConfig = async () => {
    try {
      setQuickBooksLoading(true);
      const url = getQuickBooksConfigURL(facilityId);
      const response = await makeRequest2(url, "GET");

      if (response && response.success) {
        const backendData = response.data;
        if (backendData && backendData.success) {
          setQuickBooksConnections(extractQuickBooksList(backendData));
        } else {
          setQuickBooksConnections([]);
        }
      } else {
        setQuickBooksConnections([]);
      }
      setQuickBooksLoading(false);
    } catch (error) {
      console.error("Error loading QuickBooks config:", error);
      setQuickBooksConnections([]);
      setQuickBooksLoading(false);
    }
  };

  const handleQuickBooksInputChange = (e) => {
    const { name, value } = e.target;
    setQuickBooksFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openAddQuickBooksForm = () => {
    setEditingQuickBooksId(null);
    setQuickBooksFormData(QUICKBOOKS_FORM_EMPTY);
    setShowQuickBooksForm(true);
  };

  const handleSaveQuickBooksConfig = async (e) => {
    e.preventDefault();
    try {
      setQuickBooksSubmitting(true);
      const isUpdate = !!editingQuickBooksId;
      const url = isUpdate
        ? updateQuickBooksConfigURL(facilityId)
        : saveQuickBooksConfigURL(facilityId);
      const payload = isUpdate
        ? { ...quickBooksFormData, qbConfigId: editingQuickBooksId }
        : quickBooksFormData;

      const response = await makeRequest2(url, isUpdate ? "PUT" : "POST", payload);

      if (response && response.success) {
        const backendData = response.data;
        if (backendData && backendData.success) {
          toastify(
            "success",
            backendData.message ||
              (isUpdate
                ? "QuickBooks connection updated successfully"
                : "Credentials saved! Connect this realm to authorize access."),
          );
          const newQbConfigId =
            backendData.data?.qbConfigId ||
            backendData.data?.config?._id ||
            backendData.data?._id;

          setShowQuickBooksForm(false);
          setQuickBooksFormData(QUICKBOOKS_FORM_EMPTY);
          setEditingQuickBooksId(null);
          await loadQuickBooksConfig();

          // Newly created realm: immediately offer to run the OAuth flow
          // for it, reusing the same connect handler as the table rows.
          if (!isUpdate && newQbConfigId) {
            confirmDialog({
              message: "Realm created. Connect to QuickBooks now to authorize access?",
              header: "Connect QuickBooks Realm",
              icon: "pi pi-question-circle",
              acceptLabel: "Connect Now",
              rejectLabel: "Later",
              accept: () => handleConnectQuickBooks(newQbConfigId),
            });
          }
        } else {
          toastify(
            "error",
            backendData?.error || backendData?.message || "Failed to save QuickBooks configuration",
          );
        }
      } else {
        toastify("error", response?.error || "Failed to save QuickBooks configuration");
      }
    } catch (error) {
      console.error("Error saving QuickBooks config:", error);
      toastify("error", "Failed to save QuickBooks configuration");
    } finally {
      setQuickBooksSubmitting(false);
    }
  };

  const handleConnectQuickBooks = (qbConfigId) => {
    // Full-page redirect to the backend's OAuth kickoff route, not a
    // fetch, so it needs backend_url prefixed manually (makeRequest2 does
    // this automatically for XHR calls, but this bypasses it entirely).
    const connectUrl = backend_url + connectQuickBooksURL(facilityId, qbConfigId);
    window.location.href = connectUrl;
  };

  const handleDisconnectQuickBooks = async (connection) => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect this QuickBooks realm? You'll need to reconnect to sync invoices.",
      )
    ) return;
    try {
      const url = disconnectQuickBooksURL(facilityId);
      const response = await makeRequest2(url, "POST", { qbConfigId: connection._id });

      if (response && response.success) {
        const backendData = response.data;
        if (backendData && backendData.success) {
          toastify("success", backendData.message || "QuickBooks realm disconnected successfully");
          loadQuickBooksConfig();
        } else {
          toastify("error", backendData?.error || backendData?.message || "Failed to disconnect");
        }
      } else {
        toastify("error", response?.error || "Failed to disconnect");
      }
    } catch (error) {
      console.error("Error disconnecting QuickBooks:", error);
      toastify("error", "Failed to disconnect QuickBooks");
    }
  };

  const handleEditQuickBooks = async (connection) => {
    try {
      setQuickBooksLoading(true);
      const url = `${getQuickBooksConfigURL(facilityId)}?unmask=true&qbConfigId=${connection._id}`;
      const response = await makeRequest2(url, "GET");

      if (response && response.success) {
        const backendData = response.data;
        if (backendData && backendData.success && backendData.data) {
          const config = backendData.data.config || backendData.data;
          setQuickBooksFormData({
            label:        config.label       || connection.label       || "",
            clientId:     config.clientId    || "",
            clientSecret: config.clientSecret || "",
            realmId:      config.realmId     || connection.realmId     || "",
            environment:  config.environment || connection.environment || "sandbox",
          });
          setEditingQuickBooksId(connection._id);
          setShowQuickBooksForm(true);
        } else {
          toastify(
            "error",
            backendData?.error ||
              backendData?.message ||
              "Failed to fetch QuickBooks credentials for editing",
          );
        }
      } else {
        toastify("error", response?.error || "Failed to fetch QuickBooks credentials");
      }
      setQuickBooksLoading(false);
    } catch (error) {
      console.error("Error fetching QuickBooks credentials for edit:", error);
      toastify("error", error.message || "Failed to fetch credentials");
      setQuickBooksLoading(false);
    }
  };

  const openMappingModal = (connection) => {
    // Never keep two PrimeReact Dialogs open at once - stacked masks/portals
    // can swallow clicks inside the inner one unpredictably. Close the
    // connections list modal while the mapping modal is open, and reopen it
    // when the mapping modal is dismissed (see QuickBooksMappingModal's
    // onHide below).
    setShowQuickBooksModal(false);
    setMappingQbConfigId(connection._id);
    setMappingRealmLabel(connection.label || "");
    setShowMappingModal(true);
  };

  const isQuickBooksTokenExpired = (connection) =>
    !!connection?.tokenExpiresAt && new Date(connection.tokenExpiresAt) < new Date();

  // ─── Manual sync-now (catch-up sync) ──────────────────────────────────────
  const SYNC_ENTITY_LABELS = {
    Customer: "Customers",
    Invoice: "Rent/Lease/Levy/PM Invoices",
    InvoiceCreate: "Other Invoices (VAS, Water, Power, Booking)",
    Payment: "Payments",
    InvoiceVoid: "Voided Invoices",
    Vendor: "Vendors",
  };
  const syncPollRef = useRef(null);

  useEffect(() => {
    // Stop polling if the component unmounts mid-sync.
    return () => {
      if (syncPollRef.current) {
        syncPollRef.current.cancelled = true;
        clearTimeout(syncPollRef.current.timeoutId);
      }
    };
  }, []);

  const fetchSyncStatus = async (qbConfigId) => {
    const response = await makeRequest2(
      getQuickBooksOutboundSyncStatusURL(facilityId, qbConfigId),
      "GET",
    );
    if (response?.success && response.data?.success) {
      return response.data.data?.status || {};
    }
    throw new Error(response?.data?.error || response?.error || "Failed to load sync status");
  };

  const pollSyncStatus = (qbConfigId, session) => {
    setSyncPolling(true);
    const poll = async () => {
      if (session.cancelled) return;
      try {
        const byEntity = await fetchSyncStatus(qbConfigId);
        if (session.cancelled) return;
        setSyncByEntity(byEntity);
        const stillRunning = Object.values(byEntity).some((row) => row?.status === "running");
        if (stillRunning) {
          session.timeoutId = setTimeout(poll, 2500);
        } else {
          setSyncPolling(false);
        }
      } catch (err) {
        if (session.cancelled) return;
        console.error("Error polling QuickBooks sync status:", err);
        setSyncError(err.message || "Failed to load sync status");
        setSyncPolling(false);
      }
    };
    poll();
  };

  const startSync = (qbConfigId) => {
    if (syncPollRef.current) {
      syncPollRef.current.cancelled = true;
      clearTimeout(syncPollRef.current.timeoutId);
    }
    const session = { cancelled: false, timeoutId: null };
    syncPollRef.current = session;
    pollSyncStatus(qbConfigId, session);
  };

  const openSyncModal = async (connection) => {
    setShowQuickBooksModal(false);
    setSyncQbConfigId(connection._id);
    setSyncRealmLabel(connection.label || "");
    setSyncByEntity(null);
    setSyncError(null);
    setExpandedSyncEntity(null);
    setShowSyncModal(true);
    setSyncStarting(true);
    try {
      const response = await makeRequest2(
        runQuickBooksOutboundSyncURL(facilityId, connection._id),
        "POST",
        {},
      );
      if (!(response?.success && response.data?.success)) {
        throw new Error(response?.data?.error || response?.error || "Failed to start sync");
      }
      toastify("success", response.data.message || "Sync started");
      startSync(connection._id);
    } catch (err) {
      console.error("Error starting QuickBooks sync:", err);
      setSyncError(err.message || "Failed to start sync");
    } finally {
      setSyncStarting(false);
    }
  };

  const closeSyncModal = () => {
    if (syncPollRef.current) {
      syncPollRef.current.cancelled = true;
      clearTimeout(syncPollRef.current.timeoutId);
      syncPollRef.current = null;
    }
    setShowSyncModal(false);
    setSyncQbConfigId(null);
    setSyncRealmLabel("");
    setSyncByEntity(null);
    setSyncError(null);
    setExpandedSyncEntity(null);
    setSyncPolling(false);
    setShowQuickBooksModal(true);
  };

  const getQuickBooksConnectionStatus = (connection) => {
    if (!connection?.accessToken) return "not_connected";
    if (isQuickBooksTokenExpired(connection)) return "expired";
    return "connected";
  };

  const quickBooksConnectedCount = quickBooksConnections.filter(
    (c) => getQuickBooksConnectionStatus(c) === "connected",
  ).length;

  // ─── render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <ConfirmDialog />
      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1">Third-Party Integrations</h5>
        <p className="text-muted small mb-0">
          Connect your facility with external services for accounting and tax compliance
        </p>
      </div>
      {/* Integration switcher */}
      <ul className="nav nav-pills gap-2 mb-4 flex-wrap">
        {INTEGRATION_TABS.map((t) => (
          <li className="nav-item" key={t.id}>
            <button
              className={`nav-link px-3 ${activeIntegration === t.id ? "active" : "bg-light text-dark"}`}
              onClick={() => chooseIntegration(t.id)}
            >
              <i className={`ti ${t.icon} me-1`}></i>
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {activeIntegration === "zoho" && <ZohoIntegration facilityId={facilityId} />}


      {activeIntegration === "quickbooks" && (
      <div className="row">
        {/* ── QuickBooks Card ── */}
        <div className="col-lg-6 col-md-12 mb-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-start mb-3">
                <div
                  className="avtar avtar-l bg-light-primary flex-shrink-0 me-3"
                  style={{ width: "60px", height: "60px" }}
                >
                  <i
                    className="ti ti-businessplan"
                    style={{ fontSize: "32px", color: "#2ca01c" }}
                  ></i>
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h5 className="mb-1">QuickBooks Online</h5>
                      <p className="text-muted mb-0 small">
                        Sync invoices, customers, and payments
                      </p>
                    </div>
                    {quickBooksConnectedCount > 0 ? (
                      <span className="badge bg-success">
                        <i className="ti ti-check me-1"></i>
                        {quickBooksConnectedCount} Connected
                      </span>
                    ) : (
                      <span className="badge bg-light text-dark">Not Connected</span>
                    )}
                  </div>
                </div>
              </div>

              {quickBooksLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-muted small mb-3">
                    {quickBooksConnections.length > 0
                      ? `${quickBooksConnections.length} realm(s) configured, ${quickBooksConnectedCount} connected.`
                      : "No QuickBooks companies (realms) configured yet."}
                  </p>
                  <button
                    className="btn btn-success w-100"
                    onClick={() => setShowQuickBooksModal(true)}
                  >
                    <i className="ti ti-settings me-1"></i>Manage QuickBooks Connections
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ── QuickBooks Manage Modal ── */}
      <Dialog
        header="Manage QuickBooks Connections"
        visible={showQuickBooksModal}
        onHide={() => {
          setShowQuickBooksModal(false);
          setShowQuickBooksForm(false);
          setEditingQuickBooksId(null);
          setQuickBooksFormData(QUICKBOOKS_FORM_EMPTY);
        }}
        style={{ width: "80vw", maxWidth: "900px" }}
      >
        {!showQuickBooksForm ? (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="text-muted small mb-0">
                Connect one or more QuickBooks companies (realms) to this facility.
              </p>
              <div className="d-flex gap-2">
                {quickBooksConnections.length > 1 && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    title="Choose which QuickBooks company each invoice type posts to"
                    onClick={() => {
                      setShowQuickBooksModal(false);
                      setShowRoutingModal(true);
                    }}
                  >
                    <i className="ti ti-route me-1"></i>Invoice Routing
                  </button>
                )}
                <button className="btn btn-sm btn-success" onClick={openAddQuickBooksForm}>
                  <i className="ti ti-plus me-1"></i>Add Realm
                </button>
              </div>
            </div>

            {quickBooksConnections.length === 0 ? (
              <div className="alert alert-light text-center" role="alert">
                No QuickBooks realms configured yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Environment</th>
                      <th>Realm ID</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quickBooksConnections.map((connection) => {
                      const status = getQuickBooksConnectionStatus(connection);
                      return (
                        <tr key={connection._id}>
                          <td>{connection.label || "—"}</td>
                          <td>
                            <span
                              className={`badge ${
                                connection.environment === "production"
                                  ? "bg-success-subtle text-success"
                                  : "bg-warning-subtle text-warning"
                              }`}
                            >
                              {connection.environment === "production" ? "Production" : "Sandbox"}
                            </span>
                          </td>
                          <td>{connection.realmId || "—"}</td>
                          <td>
                            {status === "connected" && (
                              <span className="badge bg-success">
                                <i className="ti ti-check me-1"></i>Connected
                              </span>
                            )}
                            {status === "expired" && (
                              <span className="badge bg-warning text-dark">
                                <i className="ti ti-alert-triangle me-1"></i>Token Expired
                              </span>
                            )}
                            {status === "not_connected" && (
                              <span className="badge bg-light text-dark">Not Connected</span>
                            )}
                          </td>
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end">
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title="Edit"
                                onClick={() => handleEditQuickBooks(connection)}
                              >
                                <i className="ti ti-edit"></i>
                              </button>
                              {status !== "connected" && (
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  title={status === "expired" ? "Reconnect" : "Connect"}
                                  onClick={() => handleConnectQuickBooks(connection._id)}
                                >
                                  <i className="ti ti-link"></i>
                                </button>
                              )}
                              {status === "connected" && (
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  title="Map Accounts & Items"
                                  onClick={() => openMappingModal(connection)}
                                >
                                  <i className="ti ti-arrows-right-left"></i>
                                </button>
                              )}
                              {status === "connected" && (
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  title="Sync Now — catch up any invoices/payments not yet synced"
                                  onClick={() => openSyncModal(connection)}
                                >
                                  <i className="ti ti-refresh"></i>
                                </button>
                              )}
                              {status !== "not_connected" && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  title="Disconnect"
                                  onClick={() => handleDisconnectQuickBooks(connection)}
                                >
                                  <i className="ti ti-unlink"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="alert alert-info mb-3" role="alert">
              <i className="ti ti-info-circle me-2"></i>
              {editingQuickBooksId
                ? "Update this QuickBooks realm's credentials"
                : "Add a new QuickBooks realm"}
            </div>

            <form onSubmit={handleSaveQuickBooksConfig}>
              <div className="mb-3">
                <label className="form-label">
                  Label <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="label"
                  value={quickBooksFormData.label}
                  onChange={handleQuickBooksInputChange}
                  placeholder="e.g. Main Office"
                  required
                />
                <small className="form-text text-muted">
                  A friendly name to tell this realm apart from others
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Client ID <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="clientId"
                  value={quickBooksFormData.clientId}
                  onChange={handleQuickBooksInputChange}
                  placeholder="AB11234567890abcdefghij"
                  required
                />
                <small className="form-text text-muted">
                  From QuickBooks Developer Dashboard
                </small>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Client Secret{" "}
                  {!editingQuickBooksId ? (
                    <span className="text-danger">*</span>
                  ) : (
                    <span className="text-muted">(leave blank to keep current)</span>
                  )}
                </label>
                <input
                  type="password"
                  className="form-control"
                  name="clientSecret"
                  value={quickBooksFormData.clientSecret}
                  onChange={handleQuickBooksInputChange}
                  placeholder={
                    editingQuickBooksId ? "Leave blank to keep current" : "Enter client secret"
                  }
                  required={!editingQuickBooksId}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Realm ID <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="realmId"
                  value={quickBooksFormData.realmId}
                  onChange={handleQuickBooksInputChange}
                  placeholder="9341456355268245"
                  required
                />
                <small className="form-text text-muted">Your QuickBooks Company ID</small>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Environment <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="environment"
                  value={quickBooksFormData.environment}
                  onChange={handleQuickBooksInputChange}
                  required
                >
                  <option value="sandbox">Sandbox (Testing)</option>
                  <option value="production">Production (Live)</option>
                </select>
              </div>

              <div className="alert alert-info mb-3" role="alert">
                <i className="ti ti-info-circle me-2"></i>
                <small>
                  <strong>Next step:</strong> After saving, use "Connect"/"Reconnect" on this
                  realm's row to authorize access and get OAuth tokens.
                </small>
              </div>

              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-success flex-fill"
                  disabled={quickBooksSubmitting}
                >
                  {quickBooksSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving…
                    </>
                  ) : (
                    <>
                      <i className="ti ti-device-floppy me-1"></i>
                      {editingQuickBooksId ? "Update" : "Save Credentials"}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setShowQuickBooksForm(false);
                    setEditingQuickBooksId(null);
                    setQuickBooksFormData(QUICKBOOKS_FORM_EMPTY);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </Dialog>

      {/* ── QuickBooks Account/Item/Payment-Channel Mapping Modal ── */}
      <QuickBooksMappingModal
        visible={showMappingModal}
        onHide={() => {
          setShowMappingModal(false);
          setMappingQbConfigId(null);
          setMappingRealmLabel("");
          setShowQuickBooksModal(true);
        }}
        facilityId={facilityId}
        qbConfigId={mappingQbConfigId}
        realmLabel={mappingRealmLabel}
      />

      <QuickBooksRoutingModal
        visible={showRoutingModal}
        onHide={() => {
          setShowRoutingModal(false);
          setShowQuickBooksModal(true);
        }}
        facilityId={facilityId}
        connections={quickBooksConnections}
      />

      {/* ── QuickBooks Sync Now / Sync Status Modal ── */}
      <Dialog
        header={`Sync Status${syncRealmLabel ? ` — ${syncRealmLabel}` : ""}`}
        visible={showSyncModal}
        onHide={closeSyncModal}
        style={{ width: "90vw", maxWidth: "700px" }}
      >
        <p className="text-muted small mb-3">
          Pushes any customers, invoices, payments, voided invoices, and vendors not yet synced
          to this realm. New invoices/payments going forward continue to sync automatically
          during generation — this is a manual catch-up for anything created before the
          connection existed, or that failed a prior automatic sync attempt.
        </p>

        {syncStarting && (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Starting sync…
          </div>
        )}

        {syncError && (
          <div className="alert alert-danger py-2 px-3 small">
            <i className="ti ti-alert-triangle me-1"></i>
            {syncError}
          </div>
        )}

        {!syncStarting && syncByEntity && (
          <>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th>Status</th>
                    <th className="text-end">Processed</th>
                    <th className="text-end">Skipped</th>
                    <th className="text-end">Errors</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(SYNC_ENTITY_LABELS).map((entity) => {
                    const row = syncByEntity[entity];
                    const status = row?.status || "not_started";
                    const details = row?.summary?.details || [];
                    const isExpanded = expandedSyncEntity === entity;
                    const badgeCls =
                      status === "completed"
                        ? "bg-success"
                        : status === "running"
                        ? "bg-info text-dark"
                        : status === "error"
                        ? "bg-danger"
                        : "bg-light text-dark";
                    return (
                      <Fragment key={entity}>
                        <tr>
                          <td>{SYNC_ENTITY_LABELS[entity]}</td>
                          <td>
                            <span className={`badge ${badgeCls}`}>
                              {status === "running" && (
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  style={{ width: "0.6rem", height: "0.6rem" }}
                                  role="status"
                                ></span>
                              )}
                              {status}
                            </span>
                            {row?.lastError && (
                              <div className="text-danger small mt-1">{row.lastError}</div>
                            )}
                          </td>
                          <td className="text-end">{row?.summary?.processed ?? "—"}</td>
                          <td className="text-end">{row?.summary?.skipped ?? "—"}</td>
                          <td className="text-end">{row?.summary?.errors ?? "—"}</td>
                          <td className="text-end">
                            {details.length > 0 && (
                              <button
                                className="btn btn-sm btn-link p-0"
                                onClick={() => setExpandedSyncEntity(isExpanded ? null : entity)}
                              >
                                {isExpanded ? "Hide" : "View"} records
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && details.length > 0 && (
                          <tr>
                            <td colSpan={6} className="bg-light-subtle p-0">
                              <div style={{ maxHeight: "200px", overflowY: "auto" }} className="p-2">
                                <table className="table table-sm mb-0">
                                  <thead>
                                    <tr>
                                      <th>Record</th>
                                      <th>Result</th>
                                      <th>Error</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {details.map((d) => (
                                      <tr key={d.id}>
                                        <td>{d.label || d.id}</td>
                                        <td>
                                          <span
                                            className={`badge ${
                                              d.status === "processed" ? "bg-success" : "bg-danger"
                                            }`}
                                          >
                                            {d.status}
                                          </span>
                                        </td>
                                        <td className="text-danger small">{d.error || ""}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {(row?.summary?.processed || 0) + (row?.summary?.errors || 0) >
                                  details.length && (
                                  <p className="text-muted small mb-0 mt-1">
                                    Showing the first {details.length} records only.
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {syncPolling ? (
              <p className="text-muted small mb-0">
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Syncing… this updates automatically.
              </p>
            ) : (
              <p className="text-success small mb-0">
                <i className="ti ti-check me-1"></i>
                Sync finished.
              </p>
            )}
          </>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          {!syncPolling && !syncStarting && syncQbConfigId && (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() =>
                openSyncModal({ _id: syncQbConfigId, label: syncRealmLabel })
              }
            >
              <i className="ti ti-refresh me-1"></i>
              Run Again
            </button>
          )}
          <button className="btn btn-sm btn-light" onClick={closeSyncModal}>
            Close
          </button>
        </div>
      </Dialog>

      {/* ── eTims Section ── */}
      {activeIntegration === "etims" && (
      <div className="mt-4">
        <div className="mb-4">
          <h5 className="mb-1">Tax Compliance</h5>
          <p className="text-muted small mb-0">
            Configure KRA eTims for electronic tax invoice submission
          </p>
        </div>
        <div className="row">
          <EtimsIntegrationTab facilityId={facilityId} />
        </div>
      </div>
      )}

      {/* ── Water Sensor Section ── */}
      {activeIntegration === "sensors" && (
      <div className="mt-4">
        <div className="mb-4">
          <h5 className="mb-1">IoT Services</h5>
          <p className="text-muted small mb-0">
            Enable the water_sensor service for this facility
          </p>
        </div>
        <div className="row">
          <WaterSensorIntegrationCard facilityId={facilityId} />
        </div>
      </div>
      )}
    </div>
  );
};

export default IntegrationsTab;